import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useMustKnowLaws, type MustKnowLaw } from "@/hooks/use-supabase-data";
import { BookOpen, Plus, Edit, Trash2, Save, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";

interface LawFormState {
  title: string;
  summary: string;
  icon: string;
  display_order: string;
  bns_section: string;
  rights_title: string;
  rights_points: string;
  proof_documents: string;
  punishment_title: string;
  punishment_points: string;
  support_title: string;
  support_points: string;
  tip: string;
}

const defaultFormState: LawFormState = {
  title: "",
  summary: "",
  icon: "shield-check",
  display_order: "",
  bns_section: "",
  rights_title: "Your Rights",
  rights_points: "",
  proof_documents: "",
  punishment_title: "If The Law Is Broken",
  punishment_points: "",
  support_title: "If You Need Help",
  support_points: "",
  tip: "",
};

const iconOptions = [
  { value: "shield-check", label: "Shield Check" },
  { value: "gavel", label: "Gavel" },
  { value: "scale", label: "Scale" },
  { value: "alert-triangle", label: "Alert Triangle" },
  { value: "user-check", label: "User Check" },
] as const;

export function MustKnowLawsManager() {
  const { laws, loading, error, refetch } = useMustKnowLaws();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLaw, setEditingLaw] = useState<MustKnowLaw | null>(null);
  const [formState, setFormState] = useState<LawFormState>(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState<"error" | "success" | "">("");

  useEffect(() => {
    if (!isDialogOpen) {
      setFormState(defaultFormState);
      setEditingLaw(null);
    }
  }, [isDialogOpen]);

  const orderedLaws = useMemo(
    () =>
      laws
        .slice()
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))
        .map((law, index) => ({ ...law, order: law.display_order ?? index })),
    [laws],
  );

  const handleRefresh = async () => {
    setStatusMessage("");
    setStatusType("");
    await refetch();
  };

  const handleInputChange =
    <K extends keyof LawFormState>(field: K) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      setFormState((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const parsePointsToText = (points: string[] | null | undefined) =>
    Array.isArray(points) ? points.join("\n") : "";

  const handleEdit = (law: MustKnowLaw) => {
    setEditingLaw(law);
    setFormState({
      title: law.title ?? "",
      summary: law.summary ?? "",
      icon: law.icon ?? "shield-check",
      display_order: law.display_order?.toString() ?? "",
      bns_section: law.bns_section ?? "",
      rights_title: law.rights_title ?? "Your Rights",
      rights_points: parsePointsToText(law.rights_points),
      proof_documents: parsePointsToText(law.proof_documents),
      punishment_title: law.punishment_title ?? "If The Law Is Broken",
      punishment_points: parsePointsToText(law.punishment_points),
      support_title: law.support_title ?? "If You Need Help",
      support_points: parsePointsToText(law.support_points),
      tip: law.tip ?? "",
    });
    setIsDialogOpen(true);
  };

  const handleAddNew = () => {
    setEditingLaw(null);
    setFormState(defaultFormState);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("must_know_laws").delete().eq("id", id);
      if (error) throw error;
      setStatusType("success");
      setStatusMessage("Law removed successfully.");
      await refetch();
    } catch (_err) {
      setStatusType("error");
      setStatusMessage("Failed to delete law. Please try again.");
    }
  };

  const splitPoints = (text: string) =>
    text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setStatusMessage("");
    setStatusType("");

    if (!formState.title.trim()) {
      setStatusType("error");
      setStatusMessage("Law title is required.");
      setSaving(false);
      return;
    }

    if (!formState.summary.trim()) {
      setStatusType("error");
      setStatusMessage("Please add a short summary.");
      setSaving(false);
      return;
    }

    const payload = {
      title: formState.title.trim(),
      summary: formState.summary.trim(),
      icon: formState.icon,
      display_order:
        formState.display_order.trim() !== ""
          ? Number.parseInt(formState.display_order.trim(), 10)
          : null,
      bns_section: formState.bns_section.trim() || null,
      rights_title: formState.rights_title.trim() || "Your Rights",
      rights_points: splitPoints(formState.rights_points),
      proof_documents: splitPoints(formState.proof_documents),
      punishment_title: formState.punishment_title.trim() || "If The Law Is Broken",
      punishment_points: splitPoints(formState.punishment_points),
      support_title: formState.support_title.trim() || "If You Need Help",
      support_points: splitPoints(formState.support_points),
      tip: formState.tip.trim() || null,
      updated_at: new Date().toISOString(),
    };

    try {
      if (editingLaw?.id) {
        const { error } = await supabase
          .from("must_know_laws")
          .update(payload)
          .eq("id", editingLaw.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("must_know_laws").insert({
          ...payload,
          created_at: new Date().toISOString(),
        });
        if (error) throw error;
      }

      setIsDialogOpen(false);
      setStatusType("success");
      setStatusMessage("Law saved successfully.");
      await refetch();
    } catch (_err) {
      setStatusType("error");
      setStatusMessage("Unable to save law. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-rose-600 to-orange-500 bg-clip-text text-transparent">
            Must Know Laws
          </h2>
          <p className="text-slate-600 text-lg">
            Keep legal protections up to date so villagers understand their rights.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading}
            className="w-full sm:w-auto hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-rose-600 to-orange-500 hover:from-rose-700 hover:to-orange-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Law
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {statusMessage && (
        <Alert
          variant={statusType === "error" ? "destructive" : "default"}
          className={
            statusType === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : undefined
          }
        >
          {statusType === "success" ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{statusMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Laws ({laws.length})
          </CardTitle>
          <CardDescription>Details shown on the public “Must Know Laws” section.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-muted-foreground">Loading laws...</div>
          ) : laws.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              No laws have been added yet. Use “Add Law” to create the first one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Summary</TableHead>
                  <TableHead>Icon</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orderedLaws.map((law) => (
                  <TableRow key={law.id}>
                    <TableCell className="font-medium">{law.title}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {law.summary}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {law.icon}
                      </Badge>
                    </TableCell>
                    <TableCell>{law.display_order ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleEdit(law)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete law?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will remove the law from the website. You can re-add it later if
                                needed.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-rose-600 to-orange-500 hover:from-rose-700 hover:to-orange-600"
                                onClick={() => handleDelete(law.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingLaw ? "Edit Law" : "Add Law"}</DialogTitle>
            <DialogDescription>
              Provide a clear summary, set of rights, and guidance so residents understand how this
              law protects them.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="law-title">Title</Label>
                <Input
                  id="law-title"
                  value={formState.title}
                  onChange={handleInputChange("title")}
                  placeholder="Police Cannot Physically Harm You"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="law-icon">Icon</Label>
                <select
                  id="law-icon"
                  className="w-full rounded-md border border-input bg-background px-3 py-2"
                  value={formState.icon}
                  onChange={handleInputChange("icon")}
                >
                  {iconOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="law-summary">Short Summary</Label>
              <Textarea
                id="law-summary"
                rows={3}
                value={formState.summary}
                onChange={handleInputChange("summary")}
                placeholder="Know what force police can use and how to report misconduct."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="law-order">Display Order (optional)</Label>
                <Input
                  id="law-order"
                  type="number"
                  value={formState.display_order}
                  onChange={handleInputChange("display_order")}
                  placeholder="e.g., 1"
                  min={0}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bns-section">BNS / Legal Reference (optional)</Label>
                <Input
                  id="bns-section"
                  value={formState.bns_section}
                  onChange={handleInputChange("bns_section")}
                  placeholder="e.g., Section 117(2) BNS"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="law-tip">Helpful Tip (optional)</Label>
                <Input
                  id="law-tip"
                  value={formState.tip}
                  onChange={handleInputChange("tip")}
                  placeholder="Ask for a written receipt of any complaint you file."
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rights-title">Rights Title</Label>
                <Input
                  id="rights-title"
                  value={formState.rights_title}
                  onChange={handleInputChange("rights_title")}
                />
                <Label htmlFor="rights-points" className="text-sm text-muted-foreground">
                  Rights (one per line)
                </Label>
                <Textarea
                  id="rights-points"
                  rows={6}
                  value={formState.rights_points}
                  onChange={handleInputChange("rights_points")}
                  placeholder={"• List the protections people should know\n• One right per line"}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="proof-documents" className="text-violet-700 font-semibold">Proof / Documents to Gather</Label>
                <div className="h-[26px]" />
                <Label htmlFor="proof-documents" className="text-sm text-muted-foreground">
                  One document per line
                </Label>
                <Textarea
                  id="proof-documents"
                  rows={6}
                  value={formState.proof_documents}
                  onChange={handleInputChange("proof_documents")}
                  placeholder={"• Medical report / photos\n• Witness names & contact\n• Complaint copy"}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="punishment-title">Consequences Title</Label>
                <Input
                  id="punishment-title"
                  value={formState.punishment_title}
                  onChange={handleInputChange("punishment_title")}
                />
                <Label htmlFor="punishment-points" className="text-sm text-muted-foreground">
                  Consequences (one per line)
                </Label>
                <Textarea
                  id="punishment-points"
                  rows={6}
                  value={formState.punishment_points}
                  onChange={handleInputChange("punishment_points")}
                  placeholder={"• What happens if authorities ignore the law\n• One point per line"}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="support-title">Support Title</Label>
                <Input
                  id="support-title"
                  value={formState.support_title}
                  onChange={handleInputChange("support_title")}
                />
                <Label htmlFor="support-points" className="text-sm text-muted-foreground">
                  Support Steps (one per line)
                </Label>
                <Textarea
                  id="support-points"
                  rows={6}
                  value={formState.support_points}
                  onChange={handleInputChange("support_points")}
                  placeholder={"• Helplines, offices, or documents to keep\n• One step per line"}
                />
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Law"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
